import { normalizeEmail } from './model';
import type { AuthPort, DataStore, InvitationCodeGenerator, SessionPort } from './ports';

export type { AuthPort, DataStore, InvitationCodeGenerator, SessionPort } from './ports';
export { createLocalObjectStoragePort } from '../storage/objectStorage';

export interface RandomSource {
  readonly getRandomValues: (bytes: Uint8Array) => Uint8Array;
}

interface StoredCredential {
  readonly userId: string;
  readonly email: string;
  readonly passwordHash: string;
}

interface StoredSession {
  readonly currentUserId: string | null;
  readonly currentChoirId: string | null;
}

const INVITATION_CODE_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const createEmptySession = (): StoredSession => ({
  currentUserId: null,
  currentChoirId: null
});

export function createLocalJsonStore<T>(
  key: string,
  createInitialValue: () => T,
  validateLoadedValue?: (value: T) => void
): DataStore<T> {
  const load = () => {
    const rawValue = localStorage.getItem(key);

    if (rawValue === null) {
      return createInitialValue();
    }

    let value: T;
    try {
      value = JSON.parse(rawValue) as T;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(`Stored data at ${key} is invalid JSON`, { cause: error });
      }

      throw error;
    }

    validateLoadedValue?.(value);
    return value;
  };

  return {
    load,
    update: async (mutator) => {
      const next = await mutator(load());
      localStorage.setItem(key, JSON.stringify(next));
      return next;
    }
  };
}

export function createLocalAuthAdapter(key: string): AuthPort {
  const store = createLocalJsonStore<readonly StoredCredential[]>(key, () => []);

  return {
    createCredential: async (input) => {
      const email = normalizeEmail(input.email);
      const passwordHash = await sha256Hex(input.password);

      await store.update((credentials) => {
        if (credentials.some((credential) => credential.email === email)) {
          throw new Error('Email is already registered');
        }

        return [
          ...credentials,
          {
            userId: input.userId,
            email,
            passwordHash
          }
        ];
      });
    },
    deleteCredential: async ({ userId }) => {
      await store.update((credentials) => {
        if (!credentials.some((credential) => credential.userId === userId)) {
          throw new Error('Credential is missing');
        }

        return credentials.filter((credential) => credential.userId !== userId);
      });
    },
    hasCredentialForEmail: async (email) => {
      const normalizedEmail = normalizeEmail(email);
      return (await store.load()).some((credential) => credential.email === normalizedEmail);
    },
    verifyCredential: async (email, password) => {
      const normalizedEmail = normalizeEmail(email);
      const passwordHash = await sha256Hex(password);
      const credential = (await store.load()).find(
        (candidate) => candidate.email === normalizedEmail && candidate.passwordHash === passwordHash
      );

      return credential?.userId ?? null;
    }
  };
}

export function createLocalSessionAdapter(key: string): SessionPort {
  const store = createLocalJsonStore<StoredSession>(key, createEmptySession);

  return {
    getCurrentUserId: async () => (await store.load()).currentUserId,
    setCurrentUserId: async (userId) => {
      await store.update((current) => ({
        ...current,
        currentUserId: userId
      }));
    },
    getCurrentChoirId: async () => (await store.load()).currentChoirId,
    setCurrentChoirId: async (choirId) => {
      await store.update((current) => ({
        ...current,
        currentChoirId: choirId
      }));
    },
    clear: async () => {
      await store.update(() => createEmptySession());
    }
  };
}

export function createLocalInvitationCodeGenerator(
  randomSource: RandomSource = crypto as RandomSource
): InvitationCodeGenerator {
  return {
    newInvitationCode: () => {
      const bytes = new Uint8Array(12);
      randomSource.getRandomValues(bytes);
      return Array.from(bytes)
        .map((byte) => INVITATION_CODE_ALPHABET[byte % INVITATION_CODE_ALPHABET.length])
        .join('');
    }
  };
}

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
